/**
 * WCC360 — Career Intelligence v3.2 content layer (read-only).
 *
 * The sealed workbook `WCC360_Career_Intelligence_Engine_v3.2` projected into
 * static JSON shards under `public/data/career_intel/`, keyed by a slug of the
 * canonical occupation name — the same name the careers bundle carries, so the
 * join is exact for all 5,877 rows.
 *
 * Presentation only. Nothing here is scored, nothing here is written. Missing
 * fields stay missing: a section we have no evidence for is not rendered.
 *
 * Regenerate with: python3 scripts/build-career-intel.py
 */
import { fixArticles, mergeBracketFragments, tidyChips, tidyItem, tidyList } from "@/lib/careerText";
import { sanitizeSalaryText } from "@/lib/salaryBands";

export interface CareerIntelAutomation {
  exposure: number | null;
  tier: string | null;
  effect: string | null;
  horizon: string | null;
  note: string | null;
}

export interface CareerIntel {
  wk: string | null;
  name: string;
  aliases: string[];
  family: string | null;
  cluster: string | null;
  level: string | null;
  iscoGroup: string | null;
  brief: string | null;
  responsibilities: string[];
  mustHave: string[];
  niceToHave: string[];
  futureSkills: string[];
  entry: string | null;
  subjects: string[];
  pathways: string[];
  salaryStart: string | null;
  salaryMid: string | null;
  salarySource: string | null;
  demand: Record<"2026" | "2030" | "2035" | "2040", string | null>;
  demandBasis: string | null;
  growthPct: number | null;
  remote: string | null;
  workLifeBalance: number | null;
  jobSatisfaction: number | null;
  freelance: string | null;
  entrepreneurship: string | null;
  automation: CareerIntelAutomation;
  humanAdvantage: string | null;
  aiResilience: number | null;
  futureReadiness: number | null;
  interests: string[];
  strengths: string[];
  archetypeMix: string | null;
  motivators: string[];
  values: string[];
  learningStyle: string | null;
  workingStyle: string | null;
  environment: string | null;
  adjacent: string[];
  stretch: string[];
  employers: string[];
  dayInLife: string | null;
  socialImpact: string | null;
  environmentalImpact: string | null;
  evidence: string | null;
  confidence: number | null;
  provenance: string | null;
  lastUpdated: string | null;
}

interface Shard {
  v: string;
  n: number;
  careers: Record<string, CareerIntel>;
}

const BASE = "/data/career_intel";

export function careerSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Mirrors `bucket_of` in scripts/build-career-intel.py. */
function bucketOf(slug: string): string {
  const head = slug.replace(/[^a-z0-9]/g, "").slice(0, 2) || "__";
  return (head + "_").slice(0, 2);
}

const shards = new Map<string, Promise<Shard | null>>();

function loadShard(bucket: string): Promise<Shard | null> {
  let p = shards.get(bucket);
  if (!p) {
    p = fetch(`${BASE}/${bucket}.json`, { cache: "force-cache" })
      .then((r) => (r.ok ? (r.json() as Promise<Shard>) : null))
      .catch(() => null);
    shards.set(bucket, p);
  }
  return p;
}

/** Career intelligence for one canonical occupation name, or null. */
export async function loadCareerIntel(name: string): Promise<CareerIntel | null> {
  const slug = careerSlug(name);
  if (!slug) return null;
  const shard = await loadShard(bucketOf(slug));
  const hit = shard?.careers?.[slug];
  if (!hit) return null;
  return {
    ...hit,
    brief: hit.brief ? tidyBrief(hit.brief) : hit.brief,
    // A band that can't be true for this seniority is dropped, never patched:
    // the page then honestly shows no figure instead of a wrong one.
    salaryStart: sanitizeSalaryText(hit.salaryStart, hit.level),
    salaryMid: sanitizeSalaryText(hit.salaryMid, hit.level),
    // The registry's lists were flattened on commas, which broke bracketed
    // asides apart. Repair them for display; the shard itself is untouched.
    responsibilities: tidyList(hit.responsibilities),
    mustHave: tidyChips(hit.mustHave),
    niceToHave: tidyChips(hit.niceToHave),
    futureSkills: tidyChips(hit.futureSkills),
    subjects: mergeBracketFragments(hit.subjects ?? []),
    pathways: tidyList(hit.pathways),
    interests: mergeBracketFragments(hit.interests ?? []),
    strengths: mergeBracketFragments(hit.strengths ?? []),
    motivators: mergeBracketFragments(hit.motivators ?? []),
    values: mergeBracketFragments(hit.values ?? []),
    adjacent: mergeBracketFragments(hit.adjacent ?? []),
    stretch: mergeBracketFragments(hit.stretch ?? []),
    employers: tidyChips(hit.employers),
  };
}


/**
 * The registry's brief is template-generated, and a handful of rows read
 * "A professional who driven by …". We repair the verb rather than rewrite
 * the sentence — the content stays the registry's, the grammar doesn't.
 */
function tidyBrief(text: string): string {
  return fixArticles(
    text
      .replace(/\bwho (driven|motivated|energised|energized|fascinated|drawn) by\b/gi, (_m, v) => `who is ${String(v).toLowerCase()} by`)
      .replace(/\s+/g, " ")
      .trim(),
  );
}


/* ---------------- display helpers ---------------- */

export const DEMAND_YEARS = ["2026", "2030", "2035", "2040"] as const;

/** 0–100 height for a demand band, so the forecast reads at a glance. */
export function demandLevel(band: string | null | undefined): number | null {
  if (!band) return null;
  const b = band.toLowerCase();
  if (b.startsWith("very high")) return 100;
  if (b.startsWith("high")) return 78;
  if (b.startsWith("medium") || b.startsWith("moderate")) return 52;
  if (b.startsWith("low")) return 28;
  if (b.startsWith("declin")) return 14;
  return null;
}

export function demandTone(band: string | null | undefined): string {
  const v = demandLevel(band);
  if (v == null) return "bg-secondary";
  if (v >= 78) return "bg-emerald-400";
  if (v >= 52) return "bg-sky-400";
  if (v >= 28) return "bg-amber-400";
  return "bg-rose-400";
}

/** "Very High in 2040, up from Medium today" style trajectory sentence. */
export function forecastSentence(intel: CareerIntel): string | null {
  const now = intel.demand["2026"];
  const then = intel.demand["2040"];
  if (!now && !then) return null;
  const a = demandLevel(now);
  const b = demandLevel(then);
  const growth =
    intel.growthPct != null ? ` Projected growth of ${Math.round(intel.growthPct)}%.` : "";
  if (a != null && b != null && b > a)
    return `Demand is expected to rise — ${now} in 2026, ${then} by 2040.${growth}`;
  if (a != null && b != null && b < a)
    return `Demand is expected to soften — ${now} in 2026, ${then} by 2040.${growth}`;
  if (then) return `Demand is expected to hold ${String(then).toLowerCase()} through 2040.${growth}`;
  return null;
}
